import styled from "styled-components/native";
import OptionHeader from "../OptionHeader";
import { DisplayConfigurationV2 } from "@src/types/DiamondDetail";
import { ProductCategory } from "@utils/constants";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "./MeasurementSchema";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import Input from "@components/atoms/Input";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { useEffect } from "react";
import { isKeyPresent } from "@scenes/configurator/JewelryConfigurator/utils";
import JewelryBuilderErrorMessage from "../JewelryBuilderErrorMessage";
import ResetButtonComponent from "../ResetButton";
import { formatNumberWithPrecision } from "@utils/product/productFormatters";
import { DefaultTheme } from "styled-components/native";

type JewelryBuilderMeasurementInputProps = {
  productType: ProductCategory;
  configObject: DisplayConfigurationV2;
  fromReviewPage?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  fromOrderCart?: boolean;
  fromRequest?: boolean;
};

interface MeasurementData {
  height: string;
  width: string;
  depth: string;
}

const JewelryBuilderMeasurementInput = ({
  productType,
  configObject,
}: JewelryBuilderMeasurementInputProps) => {
  const { t } = useScreenOptionsHooks();
  const {
    addToCartPressed,
    updateJewelryConfigInfo,
    jcSelectedOption,
    fetchValidationMessage,
    validateRequiredOptions,
  } = useJewelryConfiguratorState();
  const { control, clearErrors, getValues, setValue } =
    useForm<MeasurementData>({
      resolver: yupResolver(schema(t)),
    });

  useEffect(() => {
    configObject?.selected_item
      ? parseDimensions(configObject?.selected_item)
      : parseDimensions(
          jcSelectedOption.current.option_value[configObject.option_type]
        );
  }, [configObject?.selected_item]);

  useEffect(() => {}, [addToCartPressed]);

  const createMeasurementValue = () => {
    const values = getValues();
    if (
      Object.values(values).every(
        (v) => v !== undefined && v !== null && v !== ""
      )
    ) {
      updateJewelryConfigInfo(configObject?.option_type, [
        configObject,
        Object.values(getValues()).join(" x "),
      ]);
    } else {
      updateJewelryConfigInfo(configObject?.option_type, [
        configObject,
        null as any,
      ]);
    }
    if (
      isKeyPresent(
        configObject.option_type,
        jcSelectedOption.current.option_error
      )
    ) {
      validateRequiredOptions();
    }
  };

  const parseDimensions = (str: string) => {
    if (!str) return null;
    const [height, width, depth] = str.split(" x ");
    if ([height, width, depth].some((v) => !v)) return null;
    setValue("height", height);
    setValue("width", width);
    setValue("depth", depth);
  };

  return (
    <ContainerView>
      <OptionHeader
        title={configObject.option_name}
        value={jcSelectedOption.current.option_value[configObject.option_type]}
        productType={productType}
        helperText={configObject?.option_helper_text}
        disableToggleButton={configObject?.disable_modify_variation ?? false}
      />
      <InputContainer>
        <Controller
          control={control}
          render={({
            field: { onChange, onBlur, value, name },
            formState: { errors },
          }) => {
            return (
              <InputField>
                <Input
                  inputLabel={t(
                    "productDetailPage.jewelryConfigurator.height.text",
                    "Height"
                  )}
                  onBlur={() => {
                    if (value !== undefined && value !== null && value !== "") {
                      const num = Number(value);
                      if (!isNaN(num)) {
                        onChange(
                          formatNumberWithPrecision(num, { precision: 2 })
                        );
                      }
                    }
                    createMeasurementValue();
                    onBlur();
                  }}
                  onChangeText={(text) => {
                    const regex = /^(\d{0,2})(\.\d{0,2})?$/;
                    if (regex.test(text)) {
                      clearErrors(name);
                      onChange(text);
                    }
                  }}
                  inputType="decimal-pad"
                  value={value ?? ""}
                  error={errors?.height?.message}
                />
              </InputField>
            );
          }}
          name="height"
        />
        <Controller
          control={control}
          render={({
            field: { onChange, onBlur, value, name },
            formState: { errors },
          }) => (
            <InputField>
              <Input
                inputLabel={t(
                  "productDetailPage.jewelryConfigurator.width.text",
                  "Width"
                )}
                onBlur={() => {
                  if (value !== undefined && value !== null && value !== "") {
                    const num = Number(value);
                    if (!isNaN(num)) {
                      onChange(
                        formatNumberWithPrecision(num, { precision: 2 })
                      );
                    }
                  }
                  createMeasurementValue();
                  onBlur();
                }}
                onChangeText={(text) => {
                  const regex = /^(\d{0,2})(\.\d{0,2})?$/;
                  if (regex.test(text)) {
                    clearErrors(name);
                    onChange(text);
                  }
                }}
                inputType="decimal-pad"
                value={value ?? ""}
                error={errors?.width?.message}
              />
            </InputField>
          )}
          name="width"
        />
        <Controller
          control={control}
          render={({
            field: { onChange, onBlur, value, name },
            formState: { errors },
          }) => (
            <InputField>
              <Input
                inputLabel={t(
                  "productDetailPage.jewelryConfigurator.depth.text",
                  "Depth"
                )}
                onBlur={() => {
                  if (value !== undefined && value !== null && value !== "") {
                    const num = Number(value);
                    if (!isNaN(num)) {
                      onChange(
                        formatNumberWithPrecision(num, { precision: 2 })
                      );
                    }
                  }
                  createMeasurementValue();
                  onBlur();
                }}
                onChangeText={(text) => {
                  const regex = /^(\d{0,2})(\.\d{0,2})?$/;
                  if (regex.test(text)) {
                    clearErrors(name);
                    onChange(text);
                  }
                }}
                inputType="decimal-pad"
                value={value ?? ""}
                error={errors?.depth?.message}
              />
            </InputField>
          )}
          name="depth"
        />
      </InputContainer>
      <BottomContainer
        showError={isKeyPresent(
          configObject.option_type,
          jcSelectedOption.current.option_error
        )}
      >
        {isKeyPresent(
          configObject.option_type,
          jcSelectedOption.current.option_error
        ) && (
          <JewelryBuilderErrorMessage
            message={fetchValidationMessage(configObject.option_name, true)}
          />
        )}
        {configObject?.reset_button_visible && (
          <ResetButtonComponent productType={productType} />
        )}
      </BottomContainer>
    </ContainerView>
  );
};

export default JewelryBuilderMeasurementInput;

const ContainerView = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 8px;
  padding: 12px 16px;
  width: 100%;
  gap: 12px;
`;

const InputContainer = styled.View`
  flex-direction: row;
  gap: 16px;
`;

const InputField = styled.View`
  max-width: 125px;
  min-width: 93px;
  flex: 1;
`;

const BottomContainer = styled.View<{
  showError: boolean;
}>`
  flex-direction: row;
  justify-content: ${({ showError }: { showError: boolean }) =>
    showError ? "space-between" : "flex-end"};
  margin-top: -4px;
`;
